import { DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import Intercom, { shutdown } from '@intercom/messenger-js-sdk';
import { merge, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
} from 'rxjs/operators';
import { APP_ENVIRONMENT } from '@qtek/core/api-core';
import { AuthStore, IntegrationsService, UserStore } from '@qtek/libs/store';
import { CompanyOutput, PersonOutput, RelUserModel } from '@qtek/shared/models';

const INTERCOM_TOKEN_KEY = 'intercom_user_jwt';

function isIntercomTokenValid(token: string, userId: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (
      typeof payload.exp === 'number' &&
      payload.exp > Date.now() / 1000 &&
      typeof payload.user_id === 'string' &&
      payload.user_id.length > 0 &&
      payload.user_id === userId
    );
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class IntercomService {
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UserStore);
  private readonly integrationsService = inject(IntegrationsService);
  private readonly environment = inject(APP_ENVIRONMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  constructor() {
    const appId = this.environment.intercomAppId;
    if (!this.environment.intercomEnabled || !appId) {
      return;
    }

    // UserStore keeps the person across logout, so logout must reset the stream itself.
    merge(
      toObservable(this.userStore.person, { injector: this.injector }).pipe(
        map(person => person?.personId ?? null)
      ),
      this.authStore.loggedOut$.pipe(map((): null => null))
    )
      .pipe(
        distinctUntilChanged(),
        switchMap(personId => (personId ? this.loadToken() : of(null))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(token => {
        const person = this.userStore.currentPerson();
        const company = this.userStore.mainCompany();
        const userRelation = this.userStore.userRelation();
        if (!person || !token) return;
        this.initIntercom(appId, token, person, company, userRelation);
      });

    this.authStore.loggedOut$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        sessionStorage.removeItem(INTERCOM_TOKEN_KEY);
        shutdown();
      });
  }

  private loadToken(): Observable<string | null> {
    const cached = sessionStorage.getItem(INTERCOM_TOKEN_KEY);
    const userId = this.userStore.user()?._id;
    if (cached && userId && isIntercomTokenValid(cached, userId)) {
      return of(cached);
    }
    return this.integrationsService.getIntercomToken().pipe(
      map(({ res }) => {
        const token = res?.intercomToken ?? null;
        if (token) {
          sessionStorage.setItem(INTERCOM_TOKEN_KEY, token);
        }
        return token;
      }),
      catchError(() => {
        console.warn('[Intercom] token request failed — chat unavailable');
        return of(null);
      })
    );
  }

  private initIntercom(
    appId: string,
    token: string,
    person: PersonOutput,
    company: CompanyOutput | null,
    userRelation: RelUserModel | null
  ): void {
    Intercom({
      app_id: appId,
      user_id: userRelation?._id,
      name:
        `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() ||
        person.name,
      email: userRelation?.email,
      ...(company?.createTs
        ? { created_at: Math.floor(company.createTs / 1000) }
        : {}),
      intercom_user_jwt: token,
    });
  }
}
